import { useTranslation } from 'react-i18next';
import { Divider } from 'src/components/Divider';
import { FranchiseFeePayout } from 'src/models/hq/franchise-fee-payout';

import { PayoutSection } from './PayoutSection';

interface PayoutFinancialBreakdownProps {
  payout: FranchiseFeePayout;
}

export function PayoutFinancialBreakdown({
  payout,
}: PayoutFinancialBreakdownProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.payouts',
  });
  // Use actual transaction totals from the payout (not franchise fee breakdown)
  const grossRevenue = payout.chargesTotalInDollars || 0;
  const totalRefunds = payout.refundsTotalInDollars || 0;

  // Placeholder for tax collected - would come from actual tax data
  const taxCollected = 0;

  // Build line items for financial breakdown
  const financialLineItems = [
    {
      id: 'gross-revenue',
      label: t('grossRevenue'),
      amount: grossRevenue,
    },
    {
      id: 'refunds',
      label: t('refunds'),
      amount: totalRefunds,
    },
    ...(taxCollected !== 0
      ? [
          {
            id: 'tax-collected',
            label: t('taxCollected'),
            amount: -Math.abs(taxCollected),
          },
        ]
      : []),
  ];

  const netRevenue = grossRevenue + totalRefunds - Math.abs(taxCollected);

  // Build franchise fee line items
  const franchiseFeeLineItems =
    payout.franchiseFeeBreakdown?.map((breakdown) => ({
      id: `franchise-fee-${breakdown.franchiseFeeId}`,
      label: `${breakdown.franchiseFeeName} (${breakdown.feeAmountDisplay}):`,
      amount: breakdown.totalAmountInDollars,
    })) || [];

  return (
    <>
      <PayoutSection
        title={t('Location Revenue')}
        lineItems={financialLineItems}
        total={{
          label: t('netRevenueAfterDeductions'),
          amount: netRevenue,
        }}
      />

      {franchiseFeeLineItems.length > 0 && (
        <>
          <Divider />
          <PayoutSection
            title={t('Franchise Fees')}
            lineItems={franchiseFeeLineItems}
            payoutId={payout.id.toString()}
            feesSection={true}
            total={{
              label: t('totalFranchiseFees'),
              amount: payout.franchiseFesTotalInDollars || 0,
            }}
          />
        </>
      )}
    </>
  );
}
